import { Component, OnInit } from '@angular/core';
import { LogError, Metadata } from '@memberjunction/core';
import { ContactEntity, ContactEntityExtended, ContactRoleEntity, InstanceContactEntityExtended, OrganizationContactEntity, SubscriptionEntityExtended } from 'mj_generatedentities';
import { UserContextService } from 'src/app/core/services/user-context.service';
import { SharedService } from 'src/app/core/services/shared-service';
import { ADMIN_CONTACT_ROLE_NAME, STANDARD_CONTACT_ROLE_NAME, GUEST_CONTACT_ROLE_NAME, OWNER_CONTACT_ROLE_NAME } from '../../core/util';
import { SubscriptionPlanService, SubscriptionPreviewOptions } from 'src/app/core/services/subscription-plan.service';

interface SubscriptionWrapper {
  Subscription: SubscriptionEntityExtended;
  PlanName: string;
  IsMaxed: boolean;
  MaxedMessage: string;
}

interface RecordChange {
  OriginalContractRoleID: string;
  NewContractRoleID: string;
}

@Component({
  selector: 'app-settings-team',
  templateUrl: './team.component.html',
  styleUrls: ['./team.component.css']
})
export class TeamSettingsComponent implements OnInit{
  teamMembers = [];
  dirtyTeamMembers: Record<string, RecordChange> = {};
  contactRole: ContactRoleEntity;
  contact: ContactEntity;
  _md: Metadata;

  displayLoading: boolean = false;
  showAddUserPopup = false;
  showRemoveUserPopup = false;
  isDirty = false;

  newUserEmail = '';
  newUserLastName = '';
  newUserFirstName = '';
  newUserRole: ContactRoleEntity = null;
  allContactRoles: ContactRoleEntity[] = [];
  possibleContactRoles: ContactRoleEntity[] = [];
  guestRole: ContactRoleEntity = null;
  userToRemove: any = null;

  message = 'Loading team members...';

  newUserDialogPage: number = 1;
  availableSubscriptions: SubscriptionWrapper[] = [];
  newUserSubscriptions: string[] = [];

  currentPreviewIndex: number = 0;
  previewLoadedStatus: boolean[] = [];

  constructor(
    private userContextService: UserContextService,
    private subscriptionService: SubscriptionPlanService,
    private sharedService: SharedService
  ) {
    this._md = new Metadata();
  }

  async ngOnInit(): Promise<void> {
    // Load team members from the organization service
    this.displayLoading = true;
    this.contactRole = this.userContextService.ContactRole;
    this.contact = this.userContextService.Contact;
    this.teamMembers = await this.userContextService.GetActiveOrganizationContacts();
    this.allContactRoles = await this.userContextService.GetContactRoles();
    this.guestRole = this.allContactRoles.find((role) => role.Name === GUEST_CONTACT_ROLE_NAME);
    this.possibleContactRoles = this.getPossibleContactRoles();
    this.newUserRole = this.guestRole;
    this.displayLoading = false;

    // Load available subscriptions for the organization and filter for active ones
    const subscriptions = this.userContextService.Subscriptions.filter(sub => sub.Status === 'Active');
    this.availableSubscriptions = await Promise.all(subscriptions.map(async sub => 
    {
      const plan = await sub.GetPlan();
      const result = await sub.ValidateUserCountChange(sub.CurrentUserCount + 1);
      return {
        Subscription: sub,
        PlanName: plan.Name,
        IsMaxed: !result.IsValid,
        MaxedMessage: result.IsValid ? '' : `${result.Message}`
      }
    }));
  }

  getFields(item: any): string[] {
    return Object.keys(item);
  }

  openAddContactPopup(): void {
    this.resetNewUserForm();
    this.showAddUserPopup = true;
    this.newUserDialogPage = 1;
  }

  closeAddContactPopup(): void {
    this.showAddUserPopup = false;
  }

  resetNewUserForm(): void {
    this.newUserEmail = '';
    this.newUserFirstName = '';
    this.newUserLastName = '';
    this.newUserRole = this.guestRole;
    this.newUserSubscriptions = [];
  }

  openRemoveContactPopup(user: any): void {
    this.userToRemove = user;
    this.showRemoveUserPopup = true;
  }

  closeRemoveContactPopup(): void {
    this.showRemoveUserPopup = false;
    this.userToRemove = null;
  }

  get canEditTeam(): boolean {
    return this.contactRole.Name === ADMIN_CONTACT_ROLE_NAME || this.contactRole.Name === OWNER_CONTACT_ROLE_NAME;
  }

  canEditRole(orgContact): boolean {
    const originalRole = this.dirtyTeamMembers[orgContact.ID] ? this.allContactRoles.find(role => role.ID === this.dirtyTeamMembers[orgContact.ID].OriginalContractRoleID).Name : orgContact.ContactRole;

    if (orgContact.ContactID === this.contact.ID) {
      return false; // Cannot edit your own role
    } 
    else if (originalRole === OWNER_CONTACT_ROLE_NAME) {
      return false; // Cannot edit the owner's role
    } else if (originalRole === ADMIN_CONTACT_ROLE_NAME && this.contactRole.Name !== OWNER_CONTACT_ROLE_NAME) {
      return false; // Cannot edit an admin's role if you are not the owner
    }
    return this.contactRole.Name === ADMIN_CONTACT_ROLE_NAME || this.contactRole.Name === OWNER_CONTACT_ROLE_NAME;
  }

  canRemoveContact(orgContact): boolean {
    return this.canEditRole(orgContact);
  }

  getPossibleContactRoles(): ContactRoleEntity[] {
    if (this.contactRole.Name === OWNER_CONTACT_ROLE_NAME) {
      return this.allContactRoles;
    } else if (this.contactRole.Name === ADMIN_CONTACT_ROLE_NAME) {
      return this.allContactRoles.filter((role) => role.Name !== OWNER_CONTACT_ROLE_NAME);
    } else {
      return [];
    }
  }

  onContactRoleChange(orgContact, selectedRoleID: string): void {
    const selectedRole = this.possibleContactRoles.find(role => role.ID === selectedRoleID);
    const dirtyRecord = this.dirtyTeamMembers[orgContact.ID];

    if (selectedRole) {
      if (dirtyRecord) {
        if (dirtyRecord.OriginalContractRoleID === selectedRoleID) {
          delete this.dirtyTeamMembers[orgContact.ID];
        } else {
          dirtyRecord.NewContractRoleID = selectedRoleID;
        }
      } else {
        this.dirtyTeamMembers[orgContact.ID] = { OriginalContractRoleID: orgContact.ContactRoleID, NewContractRoleID: selectedRoleID };
      }

      orgContact.ContactRole = selectedRole.Name;
      orgContact.ContactRoleID = selectedRole.ID;
    }

    console.log(this.dirtyTeamMembers);
    this.isDirty = Object.keys(this.dirtyTeamMembers).length > 0;
  }

  onSubscriptionChange(event: Event, subscriptionID: string) {
    const input = event.target as HTMLInputElement;
    if (input.checked) {
      this.newUserSubscriptions.push(subscriptionID);
    } else {
      this.newUserSubscriptions = this.newUserSubscriptions.filter(s => s !== subscriptionID);
    }
  }

  async save(): Promise<void> {
    this.message = 'Saving changes...';
    this.displayLoading = true;

    try {
      for (const orgContactID in this.dirtyTeamMembers) {
        if (this.dirtyTeamMembers.hasOwnProperty(orgContactID)) {
          const dirtyRecord = this.dirtyTeamMembers[orgContactID];
          const orgContact = await this._md.GetEntityObject<OrganizationContactEntity>('Organization Contacts', this._md.CurrentUser);

          if (await orgContact.Load(orgContactID)) {
            orgContact.ContactRoleID = dirtyRecord.NewContractRoleID;

            if (await orgContact.Save()) {
              const teamMember = this.teamMembers.find((m) => m.ID === orgContactID);
              teamMember.ContactRoleID = dirtyRecord.NewContractRoleID;
              teamMember.ContactRole = this.allContactRoles.find((role) => role.ID === dirtyRecord.NewContractRoleID).Name;
            } else {
              this.sharedService.DisplayNotification('Error saving changes: Cannot update Organization Contact', 'error');
              LogError(`Error saving changes: Cannot update Organization Contact:\n${orgContact.LatestResult.Errors[0].Message}`);
            }
          } else {
            this.sharedService.DisplayNotification('Error saving changes: Cannot load Organization Contact', 'error');
            LogError(`Error saving changes: Cannot load Organization Contact:\n${orgContact.LatestResult.Errors[0].Message}`);
          }
        }
      }

      this.dirtyTeamMembers = {};
      this.isDirty = false;
    } catch (error) {
      this.sharedService.DisplayNotification('Error saving changes', 'error');
      LogError('Error saving changes to Organization Contacts:', error);
    } finally {
      this.displayLoading = false;
    }
  }

  async addContact(): Promise<void> {
    // On page 2, user confirms adding the new contact with selected subscriptions.
    this.message = 'Adding contact...';
    this.displayLoading = true;

    try {
      const newContact = await this.createNewContact(this.newUserFirstName, this.newUserLastName, this.newUserEmail, this.newUserRole.ID);

      if (!newContact) {
        throw new Error('Failed to create new contact');
      }

      const p1 =  this.userContextService.GetActiveOrganizationContacts();
      const p2 = this.newUserSubscriptions.map(async (subscriptionID) => {
        const subscription = this.GetSubscription(subscriptionID);
        if (subscription) {
          const success = await this.canIncreaseSubscriptionUserCountByOne(subscription);

          if (success) {
            await this.createInstanceContacts(subscription, newContact);
          }
        }
      });

      [this.teamMembers] = await Promise.all([p1, p2]);

      this.resetNewUserForm();

    } catch (error) {
      this.sharedService.DisplayNotification('Error adding contact', 'error', 3000);
      LogError('Error adding contact to Organization:', error);
    } finally {
      this.closeAddContactPopup();
      this.displayLoading = false;
    }
  }

  async removeContact(): Promise<void> {
    this.message = 'Removing contact...';
    this.displayLoading = true;

    try {
      const orgContact = await this._md.GetEntityObject<OrganizationContactEntity>('Organization Contacts');

      if (await orgContact.Load(this.userToRemove.ID)) {
        orgContact.Active = false;

        if (await orgContact.Save()) {
          this.teamMembers = this.teamMembers.filter((m) => m.ID !== this.userToRemove.ID);
        } else {
          this.sharedService.DisplayNotification('Error removing user: Cannont update corresponding Organization Contact', 'error');
        }

      } else {
        this.sharedService.DisplayNotification('Error removing user: Cannont load corresponding Organization Contact', 'error');
      }
    } catch (error) {
      LogError('Error removing contact from organization:', error);
    } finally {
      this.closeRemoveContactPopup();
      this.displayLoading = false;
    }
  }

  public isValidEmail(email: string): boolean {
    // This regex checks that the email has a valid structure: non-whitespace characters before and after the '@'
    // and at least one dot in the domain part.
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  public GetSubscription(subscriptionID: string): SubscriptionEntityExtended {
    return this.availableSubscriptions.find(sub => sub.Subscription.ID === subscriptionID)?.Subscription;
  }

  public goToPreviewPage(): void {
    this.newUserDialogPage = 2;
    this.currentPreviewIndex = 0;
    // Initialize preview loaded status for each selected subscription
    this.previewLoadedStatus = new Array(this.newUserSubscriptions.length).fill(false);
  }

  public prevPreview(): void {
    if (this.currentPreviewIndex > 0) {
      this.currentPreviewIndex--;
    }
  }

  public nextPreview(): void {
    if (this.currentPreviewIndex < this.newUserSubscriptions.length - 1) {
      this.currentPreviewIndex++;
    }
  }

  public onPreviewLoaded(isLoaded: boolean, index: number): void {
    this.previewLoadedStatus[index] = isLoaded;
  }

  public getPreviewOptions(subscriptionID: string): SubscriptionPreviewOptions {
    const subscription = this.GetSubscription(subscriptionID);
    if (subscription) {
      return {
        NewUserCount: subscription.CurrentUserCount + 1
      };
    } else {
      return {};
    }
  }

  public areAllPreviewsLoaded(): boolean {
    return this.previewLoadedStatus.every(status => status);
  }

  /**
   * Create a new contact and an organization contact associated with the organization.
   * 
   * @returns The newly created contact entity.
   * 
   * @param firstName The first name of the new contact.
   * @param lastName The last name of the new contact.
   * @param email The email address of the new contact.
   * @param contactRoleID The ID of the contact role for the new organization contact.
   * 
   */
  private async createNewContact(firstName: string, lastName: string, email: string, contactRoleID: string): Promise<ContactEntityExtended> {
    if (!this.isValidEmail(email)) {
      this.sharedService.DisplayNotification('Invalid email address', 'error', 3000);
      throw new Error('Invalid email address');
    }

    const newContact = await this._md.GetEntityObject<ContactEntity>('Contacts');
    newContact.NewRecord();
    newContact.FirstName = firstName;
    newContact.LastName = lastName;
    newContact.Email = email;

    if (await newContact.Save()) {
      const orgContact = await this._md.GetEntityObject<OrganizationContactEntity>('Organization Contacts');
      orgContact.NewRecord();
      orgContact.ContactID = newContact.ID;
      orgContact.OrganizationID = this.userContextService.Organization.ID;
      orgContact.Active = true;
      orgContact.ContactRoleID = contactRoleID;

      if (!(await orgContact.Save())) {
        const errorMessage = orgContact.LatestResult.Message || orgContact.LatestResult.Errors[0].Message;
        throw new Error(`Error adding contact: Cannot update corresponding Organization Contact:\n${errorMessage}`);
      } 

      return newContact;
    } else {
      const errorMessage = newContact.LatestResult.Message || newContact.LatestResult.Errors[0].Message;
      throw new Error (`Error adding contact: Cannot save new contact:\n${errorMessage}`);
    }
  }
  
  /**
   * Increases the user count of the given subscription by one.
   * 
   * @param subscription The subscription to update.
   * @returns True if the user count was successfully increased, false otherwise.
   */
  private async canIncreaseSubscriptionUserCountByOne(subscription: SubscriptionEntityExtended): Promise<boolean> {
    const newUserCount = subscription.CurrentUserCount + 1;
    const valResult = await subscription.ValidateUserCountChange(newUserCount);

    if (!valResult.IsValid) {
      const message = `Cannot increase user count for subscription ${subscription.Label}: ${valResult.Message}`;
      this.sharedService.DisplayNotification(message, 'error', 3000);
      LogError(message);
      return false;
    }

    return true;
  }

  /**
   * Creates instance contacts for all the instances for the given subscription and contact.
   * 
   * @param subscription The subscription for which to create instance contacts.
   * @param contact The contact for which to create instance contacts.
   * @returns A promise that resolves when the instance contacts have been created.
   */
  private async createInstanceContacts(subscription: SubscriptionEntityExtended, contact: ContactEntityExtended): Promise<void> {
    const instances = await subscription.GetInstances();

    if (!instances || instances.length === 0) {
      return;
    }

    await Promise.all(instances.map(async (instance) => {
      const ic = await this._md.GetEntityObject<InstanceContactEntityExtended>('Instance Contacts');
      ic.NewRecord();
      ic.ContactID = contact.ID;
      ic.InstanceID = instance.ID;
      ic.IsActive = true;

      if (!(await ic.Save())) {
        const errorMessage = ic.LatestResult.Message || ic.LatestResult.Errors[0].Message;
        throw new Error(`Error adding contact: Cannot create Instance Contact:\n${errorMessage}`);
      }
    }));
  }
}
